import { useState } from 'react'
import { Check, Loader2, MessageSquarePlus, Send, X } from 'lucide-react'
import { stuurSuggestie } from '../lib/api'

/* ===========================================================================
 *  Iets terugzeggen over een les
 *
 *  Waarom dit er per module staat en niet één keer ergens op een contactpagina
 *  ----------------------------------------------------------------------------
 *
 *  Omdat de fout ontdekt wordt op het moment dat je hem leest. Wie halverwege
 *  module 3 denkt "dat klopt niet met hoe wij het doen" en daarvoor terug moet
 *  naar een formulier ergens anders, denkt dat vijf seconden later niet meer.
 *
 *  Dat is hier geen theorie: deze cursus is door een AI geschreven en gaat over
 *  de wet. Er zit iets fout in -- niet misschien, maar zeker; alleen nog niet
 *  welk stuk. De enige die dat vindt is degene die het leest.
 *
 *  Waar het heen gaat
 *  ------------------
 *
 *  Naar het IT-portaal, als ticket met bron 'academy' (migratie 0238). Daar
 *  komt alles binnen wat iemand meldt -- ook wat vroeger in de meldingen van
 *  het dashboard terechtkwam (0155) -- en IT krijgt er hetzelfde seintje van
 *  als bij elk ander ticket. Geen tweede lijstje dat niemand bijhoudt.
 *
 *  Waarom er drie soorten zijn en niet één
 *  ---------------------------------------
 *
 *  "Er klopt iets niet", "ik snap het niet" en "ik heb een idee" vragen om drie
 *  verschillende reacties -- corrigeren, uitleggen, overwegen. Dat onderscheid
 *  maakt de melder toch al in zijn hoofd; hem het laten aanwijzen kost één tik
 *  en scheelt aan de andere kant het uitzoekwerk.
 * =========================================================================== */

const SOORTEN = [
  { id: 'fout',  label: 'Er klopt iets niet' },
  { id: 'vraag', label: 'Ik snap dit niet' },
  { id: 'wens',  label: 'Ik heb een idee' },
]

export default function Suggestie({
  cursus, cursusNaam, module,
}: {
  cursus: string
  cursusNaam: string
  /** De titel van de module waar dit over gaat. */
  module: string
}) {
  const [open, setOpen] = useState(false)
  const [soort, setSoort] = useState('fout')
  const [tekst, setTekst] = useState('')
  const [bezig, setBezig] = useState(false)
  const [nummer, setNummer] = useState<string | null>(null)
  const [fout, setFout] = useState(false)

  async function versturen() {
    if (bezig || tekst.trim().length < 3) return
    setBezig(true)
    setFout(false)

    const nr = await stuurSuggestie({ cursus, cursusNaam, module, soort, tekst })

    setBezig(false)
    if (nr === null) { setFout(true); return }
    setNummer(nr)
    setTekst('')
  }

  /* --- gelukt --- */
  if (nummer !== null) {
    return (
      <div className="suggestie gedaan opkomst">
        <Check size={18} />
        <span>
          <strong>Bedankt.</strong> Je bericht staat als ticket{' '}
          {nummer ? <code>{nummer}</code> : 'een ticket'} bij IT.
          Kantoor kijkt ernaar.
        </span>
      </div>
    )
  }

  /* --- dicht --- */
  if (!open) {
    return (
      <button type="button" className="suggestie-opener" onClick={() => setOpen(true)}>
        <MessageSquarePlus size={16} />
        Klopt er iets niet in deze les? Zeg het hier.
      </button>
    )
  }

  /* --- open --- */
  return (
    <div className="suggestie opkomst">
      <div className="kop">
        <strong>Over «{module}»</strong>
        <button type="button" className="knop kaal" onClick={() => setOpen(false)} aria-label="Sluiten">
          <X size={16} />
        </button>
      </div>

      <div className="soorten" role="radiogroup" aria-label="Wat is er aan de hand?">
        {SOORTEN.map((s) => (
          <button
            key={s.id}
            type="button"
            role="radio"
            aria-checked={soort === s.id}
            className={`chip ${soort === s.id ? 'aan' : ''}`}
            onClick={() => setSoort(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <label className="veldje">
        <span>Wat wil je zeggen?</span>
        <textarea
          value={tekst}
          onChange={(e) => setTekst(e.target.value)}
          rows={4}
          maxLength={4000}
          placeholder={
            soort === 'fout'
              ? 'Bijvoorbeeld: bij ons geldt die regel anders, want…'
              : soort === 'vraag'
                ? 'Bijvoorbeeld: wat betekent dit voor een chauffeur die…'
                : 'Bijvoorbeeld: zet er ook iets in over…'
          }
        />
      </label>

      {fout && (
        <div className="uitslag mis">
          <X size={16} />
          <span>
            Het is niet doorgekomen. Probeer het zo nog eens — je tekst staat
            er nog.
          </span>
        </div>
      )}

      <div className="knoppen">
        {/* Wie zijn naam eronder ziet staan, schrijft anders dan wie denkt dat
            het anoniem is. Beter dat hij dat vooraf weet dan achteraf. */}
        <span className="klein">Je naam gaat mee, zodat er teruggekoppeld kan worden.</span>
        <button
          className="knop hoofd"
          onClick={() => void versturen()}
          disabled={bezig || tekst.trim().length < 3}
        >
          {bezig ? <Loader2 size={16} className="draai" /> : <Send size={16} />}
          {bezig ? 'Bezig…' : 'Versturen'}
        </button>
      </div>
    </div>
  )
}
